/* eslint-disable @next/next/no-img-element */
/* eslint-disable no-nested-ternary */

import { cva, cx } from "cva";
import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import {
  NewsletterSignupForm,
  NewsletterSignupFormProps,
} from "~/landing-pages/components/Newsletter/NewsletterSignupForm";
import {
  heroDescriptionFontStyle,
  heroHeadlineFontStyle,
  primaryTextColorStyle,
  secondaryTextColorStyle,
} from "~/landing-pages/shared-landing-page-styles";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";

export type HeroNewsletterSignupProps = {
  blockIndex: number;
  appearance: "dark" | "light";
  backgroundImage?: string | null;
  description: string;
  headline: string;
} & Pick<NewsletterSignupFormProps, "iterableListId" | "newsletterSlug">;

const containerStyles = cva([], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      dark: "bg-gray-900",
      light: "bg-white",
    },
  },
});

export const HeroNewsletterSignup = ({
  blockIndex,
  appearance,
  backgroundImage,
  description,
  headline,
  iterableListId,
  newsletterSlug,
}: HeroNewsletterSignupProps): JSX.Element => (
  <div className={containerStyles({ appearance })}>
    <div className="relative isolate overflow-hidden px-xs pt-14 lg:px-0">
      {backgroundImage ? (
        <img
          alt=""
          src={backgroundImage}
          className="absolute inset-0 -z-10 size-full object-cover"
        />
      ) : appearance === "dark" ? (
        <div
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
          aria-hidden="true"
        >
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-brand-indigo via-brand to-brand-teal-900 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
      ) : null}
      <div className="mx-auto max-w-2xl py-12 sm:py-16 lg:py-24">
        <div className="text-center">
          <h1
            className={cx(
              heroHeadlineFontStyle(),
              primaryTextColorStyle({ appearance })
            )}
          >
            {headline}
          </h1>
          <p
            className={cx(
              heroDescriptionFontStyle(),
              secondaryTextColorStyle({ appearance })
            )}
          >
            <BuildHTML
              html={description}
              tagToClassNameMap={tagToClassNameMap}
            />
          </p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <NewsletterSignupForm
              appearance={appearance}
              blockIndex={blockIndex}
              iterableListId={iterableListId}
              newsletterSlug={newsletterSlug}
            />
          </div>
        </div>
      </div>
      {appearance === "dark" && !backgroundImage && (
        <div
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
          aria-hidden="true"
        >
          <div
            className="relative left-[calc(50%+10rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
      )}
    </div>
  </div>
);
